import { css } from '@emotion/react';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, type PropsWithChildren,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { DisclaimerContainer } from '~/_shared/components/disclaimer/disclaimer.container';
import { DisclaimerStepComponent } from '~/_shared/components/disclaimer/disclaimerStep.component';
import {
  disclaimerStepSecondaryStyle, headingSecondaryStyle, useDisclaimerSteps,
} from '~/_shared/components/disclaimer/disclaimerSteps';
import type { Theme } from '~/_shared/themes/theme.model';
import { BoundarySelectType } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.state';
import {
  s, Trans,
} from '~/translations/Trans';
import {
  MapConfirmComponent, type MapConfirmProps,
} from '../../../_shared/mapConfirm/mapConfirm.component';
import { useTranslation } from '../../../_shared/utils/hooks';

type BoundarySelectConfirmProps = Omit<MapConfirmProps, 'dropdownListStyle'|'triggerPlaceholderStyle'> & Readonly<{
  showQuickEditSteps: boolean;
  allowMultipleSelection: boolean;
  highlightedStepNumber?: number;
}>;

const disclaimerStyle = css({
  marginLeft: 20,
  padding: '23px 18px 13px',
  fontSize: '14px',
});

const stepInfoSectionStyle = css({
  display: 'flex',
  alignItems: 'center',
  paddingLeft: 4,
  gap: 4,
});

const stepInfoTextStyle = css({
  borderRadius: 4,
  padding: 4,
});

const mapConfirmStyle = css({
  minWidth: 270,
});

const triggerPlaceholderStyle = (theme: Theme) => css({
  minWidth: 112,
  color: theme.textColors.primary,
});

const dropdownListStyle = css({
  minWidth: 180,
});

const StepInfoSection = ({ children }: PropsWithChildren) => {
  return (
    <div css={stepInfoSectionStyle}>
      <FontAwesomeIcon icon={faInfoCircle} size="lg" />
      <span css={stepInfoTextStyle}>
        {children}
      </span>
    </div>
  );
};

const StepBulletPoint = ({ children }: PropsWithChildren) => {
  return (
    <p css={disclaimerStepSecondaryStyle}>
      {'- '}
      {children}
    </p>
  );
};

export const BoundarySelectConfirmComponent: FC<PropsWithChildren<BoundarySelectConfirmProps>> = ({
  highlightedStepNumber,
  showQuickEditSteps,
  allowMultipleSelection,
  children,
  ...mapConfirmProps
}) => {
  const [t] = useTranslation();
  const disclaimerSteps = useDisclaimerSteps();

  const renderSelectionSteps = () => {
    switch (true) { // This is a simple syntax for mutually exclusive conditions.
      case mapConfirmProps.selectedOptionId === BoundarySelectType.Click: {
        return (
          <DisclaimerContainer
            css={disclaimerStyle}
            permanentConfirmSettings={{
              id: showQuickEditSteps ? 'quick-edit-select-boundary-click' : 'select-boundary-click',
              text: t('Do not show again'),
            }}
          >
            <DisclaimerStepComponent
              step={1}
              isHighlighted={highlightedStepNumber === 1}
            >
              {disclaimerSteps.Start}
            </DisclaimerStepComponent>

            <DisclaimerStepComponent
              step={2}
              isHighlighted={highlightedStepNumber === 2}
            >
              {showQuickEditSteps ? (
                <>
                  <span>{t('quickEdit.step2.intro')}</span><br />
                  <Trans
                    i18nKey="quickEdit.step2.click-instructions"
                    components={{
                      strong: <strong />,
                      p1: <StepBulletPoint />,
                      p2: <StepBulletPoint />,
                      p3: <StepInfoSection />,
                    }}
                  />
                </>
              ) : t('Click the areas you wish to add to your selection.', { count: allowMultipleSelection ? 2 : 1 })}
            </DisclaimerStepComponent>

            <DisclaimerStepComponent
              step={3}
              isHighlighted={highlightedStepNumber === 3}
            >
              {disclaimerSteps.FinishSelection}
            </DisclaimerStepComponent>
          </DisclaimerContainer>
        );
      }
      case (mapConfirmProps.selectedOptionId === BoundarySelectType.HoverIdle || mapConfirmProps.selectedOptionId === BoundarySelectType.HoverActive): {
        return (
          <DisclaimerContainer
            css={disclaimerStyle}
            permanentConfirmSettings={{
              id: showQuickEditSteps ? 'quick-edit-select-boundary-hover' : 'select-boundary-hover',
              text: t('Do not show again'),
            }}
          >
            <DisclaimerStepComponent
              step={1}
              isHighlighted={highlightedStepNumber === 1}
            >
              {disclaimerSteps.Start}
            </DisclaimerStepComponent>

            <DisclaimerStepComponent
              step={2}
              isHighlighted={highlightedStepNumber === 2}
            >
              {showQuickEditSteps ? (
                <>
                  <span>{t('quickEdit.step2.intro')}</span><br />
                  <Trans
                    i18nKey="quickEdit.step2.hover-instructions"
                    components={{
                      strong: <strong />,
                      p1: <StepBulletPoint />,
                      p2: <StepBulletPoint />,
                      p3: <StepInfoSection />,
                    }}
                  />
                </>
              ) : t('Click once to start adding areas to your selection.')}
            </DisclaimerStepComponent>

            <DisclaimerStepComponent
              step={3}
              isHighlighted={highlightedStepNumber === 3}
            >
              {t('Hover your mouse over areas which you want to add to your selection.')}
            </DisclaimerStepComponent>

            <DisclaimerStepComponent
              step={4}
              isHighlighted={highlightedStepNumber === 4}
            >
              {t('Click again to stop adding areas to your selection.')}
            </DisclaimerStepComponent>

            <DisclaimerStepComponent
              step={5}
              isHighlighted={highlightedStepNumber === 5}
            >
              {disclaimerSteps.FinishSelection}
            </DisclaimerStepComponent>
          </DisclaimerContainer>
        );
      }
      case (mapConfirmProps.selectedOptionId === BoundarySelectType.Lasso): {
        return (
          <DisclaimerContainer
            css={disclaimerStyle}
            permanentConfirmSettings={{
              id: showQuickEditSteps ? 'quick-edit-select-boundary-lasso' : 'select-boundary-lasso',
              text: t('Do not show again'),
            }}
          >
            <DisclaimerStepComponent
              step={1}
              isHighlighted={highlightedStepNumber === 1}
            >
              {disclaimerSteps.Start}
            </DisclaimerStepComponent>

            <DisclaimerStepComponent
              step={2}
              isHighlighted={highlightedStepNumber === 2}
            >
              {t('Select the coverage type.')}

              <span css={disclaimerStepSecondaryStyle}>
                {'- '}
                <Trans
                  i18nKey="<0>Partial</0> requires only part of the area to be placed inside of the lasso to be selected."
                >
                  <strong css={headingSecondaryStyle}>Partial</strong>
                  {s(' requires only part of the area to be placed inside of the lasso to be selected.')}
                </Trans>
              </span>
              <span css={disclaimerStepSecondaryStyle}>
                {'- '}
                <Trans
                  i18nKey="<0>Full</0> requires entire area to be placed inside of the lasso to be selected."
                >
                  <strong css={headingSecondaryStyle}>Full</strong>
                  {s(' requires entire area to be placed inside of the lasso to be selected.')}
                </Trans>
              </span>
            </DisclaimerStepComponent>

            <DisclaimerStepComponent
              step={3}
              isHighlighted={highlightedStepNumber === 3}
            >
              {showQuickEditSteps ? (
                <>
                  <span>{t('quickEdit.step2.intro')}</span><br />
                  <Trans
                    i18nKey="quickEdit.step3.lasso-instructions"
                    components={{
                      strong: <strong />,
                      p1: <StepBulletPoint />,
                      p2: <StepBulletPoint />,
                      p3: <StepInfoSection />,
                    }}
                  />
                </>
              ) : t('Click the map to start drawing your polygon.')}
            </DisclaimerStepComponent>

            <DisclaimerStepComponent
              step={4}
              isHighlighted={highlightedStepNumber === 4}
            >
              {t('Click the map again to add additional vertices to the polygon outline.')}
            </DisclaimerStepComponent>

            <DisclaimerStepComponent
              step={5}
              isHighlighted={highlightedStepNumber === 5}
            >
              {t('Click the first vertex or double click the map to complete the loop.')}
            </DisclaimerStepComponent>

            <DisclaimerStepComponent
              step={6}
              isHighlighted={highlightedStepNumber === 6}
            >
              {disclaimerSteps.FinishSelection}
            </DisclaimerStepComponent>
          </DisclaimerContainer>
        );
      }
      default:{
        return null;
      }
    }
  };

  return (
    <MapConfirmComponent
      {...mapConfirmProps}
      triggerPlaceholderStyle={triggerPlaceholderStyle}
      css={mapConfirmStyle}
      dropdownListStyle={dropdownListStyle}
      selectionGuide={renderSelectionSteps()}
      secondRow={children}
    />
  );
};
